import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PipelineTemplate } from '@/lib/proposals/core/types';

import { getPipelineTemplate, listPipelineTemplates } from './client';

interface UsePipelineTemplatesResult {
  templates: PipelineTemplate[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  fetchByKey: (key: string) => Promise<PipelineTemplate>;
}

/**
 * Fetch the methodology templates for a record type (`entityTypeName`, e.g.
 * `ahoy_deal`). Returns an empty list when the org/record type has none.
 */
export function usePipelineTemplates(
  orgId: number | undefined,
  entityTypeName: string | undefined
): UsePipelineTemplatesResult {
  const [templates, setTemplates] = useState<PipelineTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    if (orgId === undefined || !entityTypeName) {
      setIsLoading(false);
      setTemplates([]);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    listPipelineTemplates(orgId, entityTypeName, controller.signal)
      .then(data => {
        setTemplates(data);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [orgId, entityTypeName, fetchTick]);

  const refetch = useCallback(() => {
    setFetchTick(t => t + 1);
  }, []);

  const fetchByKey = useCallback(
    (key: string): Promise<PipelineTemplate> => {
      if (orgId === undefined || !entityTypeName) {
        return Promise.reject(
          new Error('orgId and entityTypeName are required')
        );
      }
      return getPipelineTemplate(orgId, key, entityTypeName);
    },
    [orgId, entityTypeName]
  );

  return useMemo(
    () => ({ templates, isLoading, error, refetch, fetchByKey }),
    [templates, isLoading, error, refetch, fetchByKey]
  );
}
